import { Menu } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  IconButton,
  useMediaQuery,
  Typography,
} from "@mui/material";
import React, { PropsWithChildren } from "react";
import Drawer from "./drawer";
import Breadcrumb from "../../components/breadcrumbs/breadcrubm";
import MobileDrawer from "./mobileDrawer";
import useUser from "../../providers/user_provider/user_provider";
import { useTheme } from "@mui/material/styles";

interface AdminDashboardLayoutProps { }

function AdminDashboardLayout({
  children,
}: PropsWithChildren<AdminDashboardLayoutProps>) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { setMobileDrawerOpen, mobileDrawerOpen } = useUser();
  const sidebarWidth = 280;

  const handleDrawerOpen = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #dff1f6 0%, #edf7fb 34%, #f6fbfd 100%)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1680,
          mx: "auto",
          px: { xs: 1.5, sm: 2.5, md: 3 },
          py: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: { xs: 2, md: 3 },
          }}
        >
          {isDesktop ? (
            <Card
              sx={{
                width: sidebarWidth,
                flexShrink: 0,
                borderRadius: 4,
                overflow: "hidden",
                position: "sticky",
                top: 24,
                height: "calc(100vh - 48px)",
                boxShadow: "0 24px 56px rgba(14, 65, 87, 0.12)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  bgcolor: "#ffffff",
                }}
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    px: 2.5,
                    py: 2.5,
                    borderBottom: "1px solid rgba(10, 61, 82, 0.08)",
                    background:
                      "linear-gradient(135deg, rgba(0,133,219,0.08), rgba(72,187,120,0.08))",
                  }}
                >
                  <Avatar src="/images/icons/sammitra_logo.png" />
                  <Box>
                    <Typography fontWeight={800}>SAMMITRA</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Admin dashboard
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 1.5,
                    overflowY: "auto",
                    overflowX: "hidden",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <Drawer />
                </Box>
              </Box>
            </Card>
          ) : null}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!isDesktop ? <MobileDrawer /> : null}

            <Card
              sx={{
                borderRadius: 4,
                boxShadow: "0 18px 42px rgba(14, 65, 87, 0.08)",
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  minHeight: 72,
                  bgcolor: "white",
                  borderBottom: "1px solid rgba(10, 61, 82, 0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: { xs: 2, md: 3 },
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  {!isDesktop ? (
                    <IconButton
                      color="primary"
                      aria-label="open drawer"
                      edge="start"
                      onClick={handleDrawerOpen}
                      sx={{
                        ...(mobileDrawerOpen && { display: "none" }),
                      }}
                    >
                      <Menu />
                    </IconButton>
                  ) : null}
                  <Box>
                    <Typography fontWeight={800}>Administration</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage portal content and submissions
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>

            <Card
              sx={{
                mt: 2,
                borderRadius: 4,
                minHeight: "calc(100vh - 170px)",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 24px 56px rgba(14, 65, 87, 0.1)",
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  pt: { xs: 2, md: 2.5 },
                }}
              >
                <Breadcrumb />
              </Box>
              <Box
                sx={{
                  px: { xs: 1, sm: 1.5, md: 2 },
                  pb: { xs: 2.5, md: 3 },
                  flex: 1,
                  minWidth: 0,
                  overflow: "visible",
                }}
              >
                {children}
              </Box>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AdminDashboardLayout;
